/**
 * Element editing utilities for canvas undo/redo functionality
 * Extracted from MyCanvas.js to reduce file size
 */

/**
 * Create a function to start element editing
 * @param {boolean} isElementBeingEdited - Current editing state
 * @param {Object} selectedObject - Currently selected object
 * @param {Function} setIsElementBeingEdited - Setter for editing state
 * @param {Function} setPendingChanges - Setter for pending changes flag
 * @param {React.RefObject} pendingChangesRef - Ref for pending changes (cleared when resetting so endElementEditing stays in sync)
 * @param {Function} setInitialEditState - Setter for initial edit state
 * @param {Array} texts - Current texts array
 * @param {Array} rectangles - Current rectangles array
 * @param {Array} circles - Current circles array
 * @param {Array} arrows - Current arrows array
 * @param {Array} doubleSidedArrows - Current double-sided arrows array
 * @param {Array} images - Current images array
 * @param {Array} scribbles - Current scribbles array
 * @param {Array} lines - Current lines array
 * @returns {Function} Function to start element editing
 */
export const createStartElementEditing =
  (
    isElementBeingEdited,
    selectedObject,
    setIsElementBeingEdited,
    setPendingChanges,
    pendingChangesRef,
    pendingChanges, // Add pendingChanges to read current value
    setInitialEditState,
    texts,
    rectangles,
    circles,
    arrows,
    doubleSidedArrows,
    images,
    scribbles,
    lines
  ) =>
    () => {
      // DEBUG: Log when startElementEditing is called
      if (!isElementBeingEdited && selectedObject) {
        setIsElementBeingEdited(true);
        // CRITICAL: Don't reset pendingChanges if it's already true (e.g., from drag transfer)
        // Only reset it to false when starting a fresh edit session
        if (!pendingChanges) {
          setPendingChanges(false);
          if (pendingChangesRef) pendingChangesRef.current = false;
        } else {
        }
        // Store initial state but don't save to history yet (only save when there are actual changes)
        setInitialEditState({
          texts: JSON.parse(JSON.stringify(texts)),
          rectangles: JSON.parse(JSON.stringify(rectangles)),
          circles: JSON.parse(JSON.stringify(circles)),
          arrows: JSON.parse(JSON.stringify(arrows)),
          doubleSidedArrows: JSON.parse(JSON.stringify(doubleSidedArrows)),
          images: images.map((img) => ({
            ...img,
            image: null,
            originalImage: null,
            compressedImage: null,
          })),
          scribbles: JSON.parse(JSON.stringify(scribbles)),
          lines: JSON.parse(JSON.stringify(lines)),
        });
      }
    };

/**
 * Create a function to end element editing
 * @param {React.RefObject} isElementBeingEditedRef - Ref to current editing state
 * @param {React.RefObject} pendingChangesRef - Ref to current pending changes state (to read current value)
 * @param {React.RefObject} isTextEditingModeRef - Ref to track if we're in text editing mode
 * @param {React.RefObject} latestUpdatedImagesRef - Ref to store latest updated images array from transform operations
 * @param {React.RefObject} latestUpdatedTextsRef - Ref to store latest updated texts array from transform operations
 * @param {React.RefObject} offCanvasImagesRef - Ref updated each render with current offCanvasImages (so drag position is captured after 200ms)
 * @param {Function} setRectangles - Setter for rectangles array (used to read current state)
 * @param {Function} setOffCanvasRectangles - Setter for off-canvas rectangles array (used to read current state)
 * @param {Function} setCircles - Setter for circles array (used to read current state)
 * @param {Function} setOffCanvasCircles - Setter for off-canvas circles array (used to read current state)
 * @param {Function} saveToHistory - Function to save to history
 * @param {Function} setIsElementBeingEdited - Setter for editing state
 * @param {Function} setPendingChanges - Setter for pending changes flag
 * @param {Function} setInitialEditState - Setter for initial edit state
 * @param {Function} setImages - Optional: setter for main images (so we can apply ref and save when selection cleared after crop)
 * @param {Function} setOffCanvasImages - Optional: setter for off-canvas images
 * @param {React.RefObject} lastHistoryOperationTypeRef - Optional: ref updated on each history push; when add_image/add_preset_image, skip save to avoid 2 undos
 * @returns {Function} Function to end element editing
 */
export const createEndElementEditing =
  (
    isElementBeingEditedRef,
    pendingChangesRef,
    isTextEditingModeRef,
    latestUpdatedImagesRef,
    latestUpdatedTextsRef,
    offCanvasImagesRef,
    setRectangles,
    setOffCanvasRectangles,
    setCircles,
    setOffCanvasCircles,
    saveToHistory,
    setIsElementBeingEdited,
    setPendingChanges,
    setInitialEditState,
    isSavingRef = null, // Optional ref to prevent duplicate saves
    getSelectedObject = null, // Optional: function to get current selected object
    isTransformingRef = null, // Optional: ref to clear transform initial state
    setImages = null, // Optional: apply ref and save when selection cleared after crop/transform
    setOffCanvasImages = null,
    lastHistoryOperationTypeRef = null,
    setTexts = null, // Optional: read current texts in delayed save (e.g. text stroke change then unselect)
    setOffCanvasTexts = null,
    latestImageEditorRefUrlRef = null // Optional: ref { imageId, referenceUrl } updated by ImageEditor so we preserve referenceUrl when applying ref before state has flushed
  ) =>
    () => {
      // DEBUG: Log when endElementEditing is called
      // Read current values from refs to avoid stale closure issues
      const isEditing = isElementBeingEditedRef?.current ?? false;
      const hasPendingChanges = pendingChangesRef?.current ?? false;
      // Capture so setTimeout still saves if another effect clears pendingChangesRef before timeout runs
      const hadPendingChangesAtInvocation = hasPendingChanges;
      const isTextEditing = isTextEditingModeRef?.current ?? false;
      // Get current selected object if getter function is provided
      const currentSelectedObject = getSelectedObject ? getSelectedObject() : null;
      const hasSelectedObject = !!currentSelectedObject; // Check if element is currently selected

      // CRITICAL FIX: If no selected object and no pending changes, just clear the editing state immediately
      // This prevents the first click from being consumed by endElementEditing when there's nothing to save
      // This fixes the two-click deselection issue after drag-to-create
      if (!hasSelectedObject && !hasPendingChanges) {
        setIsElementBeingEdited(false);
        setPendingChanges(false);
        if (pendingChangesRef) pendingChangesRef.current = false;
        setInitialEditState(null);
        if (isTextEditingModeRef) {
          isTextEditingModeRef.current = false;
        }
        return;
      }

      // Pending changes but no selected object: often selection cleared before save (e.g. crop then click background, or text stroke change then unselect).
      // If we have pending image updates in the ref (crop/transform), apply them and save. Otherwise fall through so the delayed save path runs (captures text/rect/circle state).
      if (!hasSelectedObject && hasPendingChanges) {
        const refMain = latestUpdatedImagesRef?.current?.main;
        const refOffCanvas = latestUpdatedImagesRef?.current?.offCanvas;
        const lastOp = lastHistoryOperationTypeRef?.current;
        // Treat plain add_image/add_image_clone as \"add then no-op\" so we can
        // skip redundant saves right after adding. For add_preset_image we
        // always want the first real edit (e.g. replace image) to create a
        // new history entry so undo goes back to the preset image instead of
        // clearing it entirely.
        const lastWasAddImage =
          lastOp === "add_image" || lastOp === "add_image_clone";
        const refHasCropToSave = (refOffCanvas?.some((img) => img?.cropData)) || (refMain?.some((img) => img?.cropData));
        const hasTransformChange = (img) =>
          (img?.rotation != null && Number(img.rotation) !== 0) ||
          (img?.scaleX != null && Math.abs(Number(img.scaleX)) !== 1) ||
          (img?.scaleY != null && Math.abs(Number(img.scaleY)) !== 1);
        const refHasTransformChanges =
          (Array.isArray(refMain) && refMain.some(hasTransformChange)) ||
          (Array.isArray(refOffCanvas) && refOffCanvas.some(hasTransformChange));
        const skipSaveAsRedundant =
          lastWasAddImage && !refHasCropToSave && !refHasTransformChanges;
        if (
          (refMain && setImages) ||
          (refOffCanvas && setOffCanvasImages)
        ) {
          // CRITICAL: Merge ref with current state so position (x, y) is preserved after drag.
          // Ref was set at crop/transform time; drag end updates state but not ref, so we must not overwrite position.
          // Iterate over current state (prev) so we never re-add an image that was transferred to the other canvas
          // (e.g. main→off would have removed from images; applying refMain.map would re-add it and create duplicate).
          // Preserve editor-only fields (e.g. referenceUrl) from current state or editor ref when ref doesn't have them
          // (user may have typed referenceUrl but clicked off-canvas before blur/debounce flushed to state)
          const mergeImageWithRef = (prevImg, refImg) => {
            const fromEditor =
              latestImageEditorRefUrlRef?.current?.imageId === prevImg.id
                ? latestImageEditorRefUrlRef.current.referenceUrl
                : undefined;
            const merged = {
              ...prevImg,
              ...refImg,
              x: prevImg.x,
              y: prevImg.y,
              referenceUrl: refImg.referenceUrl ?? prevImg.referenceUrl ?? fromEditor ?? "",
            };
            // Safety net: the ref may have been clobbered by a background compression
            // callback that used a stale images closure (carrying either no cropData, or
            // the cropData from a previous crop before a Reset). Detect this by comparing
            // dimensions: if the current state (prevImg) has cropData and its dimensions
            // differ from the ref's dimensions, the ref is stale. Trust the current state.
            if (
              prevImg.cropData != null &&
              (prevImg.width !== refImg.width || prevImg.height !== refImg.height)
            ) {
              merged.cropData = prevImg.cropData;
              merged.width = prevImg.width;
              merged.height = prevImg.height;
              merged.originalWidth = prevImg.originalWidth;
              merged.originalHeight = prevImg.originalHeight;
              merged.originalAspectRatio = prevImg.originalAspectRatio;
              merged.imageDataUrl = prevImg.imageDataUrl;
              merged.image = prevImg.image;
              merged.src = prevImg.src;
              merged.originalImageDataUrl = prevImg.originalImageDataUrl;
              merged.originalCropRotation = prevImg.originalCropRotation;
              merged.flipHorizontal = prevImg.flipHorizontal;
              merged.flipVertical = prevImg.flipVertical;
              merged.hasBakedTransforms = prevImg.hasBakedTransforms;
            }
            return merged;
          };
          if (refOffCanvas && setOffCanvasImages) {
            setOffCanvasImages((prev) => {
              if (!Array.isArray(prev)) return refOffCanvas;
              return prev.map((prevImg) => {
                const refImg = refOffCanvas.find((r) => r.id === prevImg.id);
                return refImg ? mergeImageWithRef(prevImg, refImg) : prevImg;
              });
            });
          }
          if (refMain && setImages) {
            setImages((prev) => {
              if (!Array.isArray(prev)) return refMain;
              return prev.map((prevImg) => {
                const refImg = refMain.find((r) => r.id === prevImg.id);
                return refImg ? mergeImageWithRef(prevImg, refImg) : prevImg;
              });
            });
          }
          if (!skipSaveAsRedundant) {
            const mainOverride = refMain ?? null;
            const offCanvasOverride = refOffCanvas ?? null;
            saveToHistory(
              "element_edit_complete",
              mainOverride,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              offCanvasOverride
            );
          }
          setIsElementBeingEdited(false);
          setPendingChanges(false);
          if (pendingChangesRef) pendingChangesRef.current = false;
          setInitialEditState(null);
          if (isTextEditingModeRef) {
            isTextEditingModeRef.current = false;
          }
          return;
        }
        // No image refs (e.g. text stroke/property change) - fall through so shouldSave runs and setTimeout captures current texts/rects/circles
      }

      // CRITICAL: Prevent duplicate calls - if we're already saving, skip
      if (isSavingRef?.current) {
        return;
      }

      // CRITICAL: Check if text was added by comparing current text with initial state or checking if text exists
      // Text addition changes text from "" (empty) to "Sample Text" (or other value), which is a change
      const currentText = currentSelectedObject?.text || "";
      const hasText = currentText !== "" && currentText !== null;

      // CRITICAL: If we're in text editing mode, we need to check if text was actually added/modified
      // Text addition changes text from "" (empty) to "Sample Text" (or other value), which is a change
      // Even if hasPendingChanges is false (might have been reset), if text editor was open, we should save
      // This ensures text addition is always saved, even if pendingChanges flag wasn't set correctly
      if (isTextEditing) {
        // Check if the selected object has text (meaning text was added)

        if (!hasText && !hasPendingChanges) {
          // Text editor was open but no text was added and no pending changes - skip save
          setIsElementBeingEdited(false);
          setPendingChanges(false);
          if (pendingChangesRef) pendingChangesRef.current = false;
          setInitialEditState(null);
          isTextEditingModeRef.current = false; // Reset the flag
          return;
        } else {
          // Text was added (hasText) OR there are pending changes - we need to save
          // Reset text editing flag but continue to save
          isTextEditingModeRef.current = false;
        }
      }

      // Save if:
      // 1. There are pending changes, OR
      // 2. Element is being edited AND has pending changes (actually modified), OR
      // 3. Element is selected AND has text (text was added)
      // CRITICAL: Don't save if just selecting an element without any changes
      // This prevents false positives when clicking background to unselect
      const shouldSave = hasPendingChanges ||
        (isEditing && hasSelectedObject && hasPendingChanges) ||
        (hasSelectedObject && hasText);

      // DEBUG: Log the shouldSave decision
      // CRITICAL: Double-check that we should actually save
      // Don't save if we're just unselecting (no selected object, no pending changes, no text)
      if (shouldSave && !hasSelectedObject && !hasPendingChanges && !hasText) {
        setIsElementBeingEdited(false);
        setPendingChanges(false);
        if (pendingChangesRef) pendingChangesRef.current = false;
        setInitialEditState(null);
        return;
      }

      if (shouldSave) {
        // Mark that we're saving to prevent duplicate calls
        if (isSavingRef) {
          isSavingRef.current = true;
        }

        // Save final state when element editing ends (only if there were actual changes)
        // Use setTimeout to ensure state updates are complete
        // Increased delay to 200ms to ensure all state updates (including text addition) are fully applied
        // This is critical for text addition which happens asynchronously via setState
        setTimeout(() => {
          // CRITICAL: Double-check that we still have actual changes before saving
          // This prevents false positives when unselecting (selectedObject might have been cleared by now)
          const currentSelectedObjectCheck = getSelectedObject ? getSelectedObject() : null;
          const stillHasSelectedObject = !!currentSelectedObjectCheck;
          const stillHasPendingChanges = pendingChangesRef?.current ?? false;
          const currentTextCheck = currentSelectedObjectCheck?.text || "";
          const stillHasText = currentTextCheck !== "" && currentTextCheck !== null;

          // If we no longer have a selected object and no pending changes and no text, don't save.
          // Use hadPendingChangesAtInvocation so we still save when there were pending changes at
          // invocation but the ref was cleared (e.g. by selection effect) before this timeout ran.
          if (!stillHasSelectedObject && !stillHasPendingChanges && !stillHasText && !hadPendingChangesAtInvocation) {
            setIsElementBeingEdited(false);
            setPendingChanges(false);
            if (pendingChangesRef) pendingChangesRef.current = false;
            setInitialEditState(null);
            if (isSavingRef) {
              isSavingRef.current = false;
            }
            return;
          }

          // CRITICAL: Use latestUpdatedImagesRef and latestUpdatedTextsRef if available to ensure rotation and other transform properties are saved
          // Also read current rectangles/circles state to ensure text property changes (including formatting) are saved
          // This ensures element_edit_complete uses the updated state, not the stale closure value
          const updatedImagesMain = latestUpdatedImagesRef?.current?.main;
          const updatedImagesOffCanvas = latestUpdatedImagesRef?.current?.offCanvas;
          const updatedTextsMain = latestUpdatedTextsRef?.current?.main;
          const updatedTextsOffCanvas = latestUpdatedTextsRef?.current?.offCanvas;

          // CRITICAL: Read current rectangles and circles state using functional updates
          // This ensures we capture the latest state including text property with HTML formatting
          let currentRectangles = null;
          let currentOffCanvasRectangles = null;
          let currentCircles = null;
          let currentOffCanvasCircles = null;

          setRectangles((rects) => {
            currentRectangles = rects;
            return rects; // Return same to avoid triggering re-render
          });

          setOffCanvasRectangles((rects) => {
            currentOffCanvasRectangles = rects;
            return rects; // Return same to avoid triggering re-render
          });

          setCircles((circles) => {
            currentCircles = circles;
            return circles; // Return same to avoid triggering re-render
          });

          setOffCanvasCircles((circles) => {
            currentOffCanvasCircles = circles;
            return circles; // Return same to avoid triggering re-render
          });

          let currentTexts = null;
          let currentOffCanvasTexts = null;
          if (setTexts) {
            setTexts((prev) => {
              currentTexts = prev;
              return prev;
            });
          }
          if (setOffCanvasTexts) {
            setOffCanvasTexts((prev) => {
              currentOffCanvasTexts = prev;
              return prev;
            });
          }

          // CRITICAL: Log what we're capturing to debug text addition issues
          // Check for text in both rectangles and circles
          const sampleRectWithText = currentRectangles?.find(r => r.text && r.text !== "") ||
            currentOffCanvasRectangles?.find(r => r.text && r.text !== "");
          const sampleCircleWithText = currentCircles?.find(c => c.text && c.text !== "") ||
            currentOffCanvasCircles?.find(c => c.text && c.text !== "");

          // CRITICAL: Log detailed property information to debug align/verticalAlign issues
          // Also check all rectangles/circles for align properties, not just ones with text
          const allRectsWithAlign = [
            ...(currentRectangles || []),
            ...(currentOffCanvasRectangles || [])
          ].filter(r => r.align || r.verticalAlign);
          const allCirclesWithAlign = [
            ...(currentCircles || []),
            ...(currentOffCanvasCircles || [])
          ].filter(c => c.align || c.verticalAlign);

          const sampleRectDetails = sampleRectWithText ? {
            id: sampleRectWithText.id,
            hasText: true,
            text: sampleRectWithText.text?.substring(0, 20),
            align: sampleRectWithText.align,
            verticalAlign: sampleRectWithText.verticalAlign,
            textColor: sampleRectWithText.textColor,
            fontSize: sampleRectWithText.fontSize,
            fontStyle: sampleRectWithText.fontStyle,
            textDecoration: sampleRectWithText.textDecoration,
            allKeys: Object.keys(sampleRectWithText),
          } : null;
          const sampleCircleDetails = sampleCircleWithText ? {
            id: sampleCircleWithText.id,
            hasText: true,
            text: sampleCircleWithText.text?.substring(0, 20),
            align: sampleCircleWithText.align,
            verticalAlign: sampleCircleWithText.verticalAlign,
            textColor: sampleCircleWithText.textColor,
            fontSize: sampleCircleWithText.fontSize,
            fontStyle: sampleCircleWithText.fontStyle,
            textDecoration: sampleCircleWithText.textDecoration,
            allKeys: Object.keys(sampleCircleWithText),
          } : null;


          // Always pass updated arrays if we have them (images, texts, rectangles, circles)
          // This ensures all property changes including text formatting and text addition are saved
          // CRITICAL: Always save if we have any rectangles/circles (even if empty arrays) to ensure text addition is captured
          // The functional updates above ensure we get the latest state including any text that was just added
          // CRITICAL: Use stillHasPendingChanges (from ref) instead of hasPendingChanges (from closure) to get current value
          // This ensures drag operations that set pendingChanges after endElementEditing starts still trigger a save
          if (updatedImagesMain || updatedImagesOffCanvas || updatedTextsMain || updatedTextsOffCanvas ||
            currentRectangles !== null || currentOffCanvasRectangles !== null ||
            currentCircles !== null || currentOffCanvasCircles !== null || stillHasPendingChanges ||
            hadPendingChangesAtInvocation) {
            // Pass the updated arrays as overrides to ensure all changes are saved
            // When refs are null (e.g. text stroke change), use current state from setTexts/setOffCanvasTexts read above
            const params = new Array(17).fill(null);
            params[0] = "element_edit_complete"; // operationType
            params[1] = updatedImagesMain; // currentImagesOverride (index 1)
            params[5] = updatedTextsMain ?? currentTexts; // currentTextsOverride (index 5) - use current state when no ref (e.g. text property change)
            params[8] = currentRectangles; // currentRectanglesOverride (index 8) - CRITICAL: ensures text property is saved
            params[9] = currentOffCanvasRectangles; // currentOffCanvasRectanglesOverride (index 9)
            params[10] = currentCircles; // currentCirclesOverride (index 10) - CRITICAL: ensures text property is saved
            params[11] = currentOffCanvasCircles; // currentOffCanvasCirclesOverride (index 11)
            params[15] = updatedTextsOffCanvas ?? currentOffCanvasTexts; // currentOffCanvasTextsOverride (index 15)
            // Use transform ref if set; else use offCanvasImagesRef (latest after 200ms) so drag position is saved
            params[16] = updatedImagesOffCanvas ?? offCanvasImagesRef?.current ?? undefined; // currentOffCanvasImagesOverride (index 16)

            // Count rectangles and circles with text (both main and off-canvas)
            const rectanglesWithText = [
              ...(currentRectangles?.filter(r => r.text && r.text !== "") || []),
              ...(currentOffCanvasRectangles?.filter(r => r.text && r.text !== "") || [])
            ].length;
            const circlesWithText = [
              ...(currentCircles?.filter(c => c.text && c.text !== "") || []),
              ...(currentOffCanvasCircles?.filter(c => c.text && c.text !== "") || [])
            ].length;


            saveToHistory(...params);
            // Clear the refs after using them
            if (latestUpdatedImagesRef) {
              latestUpdatedImagesRef.current = { main: null, offCanvas: null };
            }
            if (latestUpdatedTextsRef) {
              latestUpdatedTextsRef.current = { main: null, offCanvas: null };
            }
          } else {
            // Fallback to normal save if no updated arrays are stored
            saveToHistory("element_edit_complete");
          }
          setIsElementBeingEdited(false);
          setPendingChanges(false);
          if (pendingChangesRef) pendingChangesRef.current = false;
          setInitialEditState(null);

          // CRITICAL: Clear transform initial state after save completes
          // This ensures next editing session starts fresh
          if (isTransformingRef && isTransformingRef.current && isTransformingRef.current.initialTransformState) {
            delete isTransformingRef.current.initialTransformState;
          }

          // Clear the saving flag after a delay to allow for the save to complete
          if (isSavingRef) {
            setTimeout(() => {
              isSavingRef.current = false;
            }, 200); // Clear flag after save completes
          }
        }, 200); // Increased delay to 200ms to ensure all state updates (including text addition) are fully applied
      } else if (isEditing) {
        // No changes were made, just clear the editing state without saving
        // This prevents blank states from being saved when just deselecting
        setIsElementBeingEdited(false);
        setPendingChanges(false);
        if (pendingChangesRef) pendingChangesRef.current = false;
        setInitialEditState(null);

        // CRITICAL: Also clear transform initial state when no changes were made
        if (isTransformingRef && isTransformingRef.current && isTransformingRef.current.initialTransformState) {
          delete isTransformingRef.current.initialTransformState;
        }
      }
    };
